import { useState, type CSSProperties, type ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import simbolo from '@planilla/ui/marca/simbolo.svg';
import { enDemostracion } from '../api/cliente';
import { useTema, type Tema } from '../comun/tema';
import { useSesion, useYo } from '../sesion/sesion';
import { Boton } from './Controles';
import { Icono } from './Icono';
import { Avatar } from './Piezas';

const TEMAS: Array<{ valor: Tema; icono: string; nombre: string }> = [
  { valor: 'sistema', icono: 'monitor', nombre: 'Seguir al sistema' },
  { valor: 'claro', icono: 'sol', nombre: 'Tema claro' },
  { valor: 'oscuro', icono: 'luna', nombre: 'Tema oscuro' },
];

/**
 * El marco de la aplicación: barra lateral azul profunda y el contenido sobre el papel
 * azulado. El menú NO está escrito acá: se recorre `yo.menu`, que la API calcula como
 * permisos ∩ módulos del plan. Un módulo que la entidad no compró se muestra con candado y
 * lleva a "no está en tu plan" — que es una venta, no un callejón.
 */
export function Marco({ children }: { children: ReactNode }) {
  const yo = useYo();
  const sesion = useSesion();
  const { tema, cambiar } = useTema();
  const [selector, setSelector] = useState(false);
  const [abierta, setAbierta] = useState(false);
  const ubicacion = useLocation();

  const otrasEntidades = yo.entidades.filter((entidad) => entidad.id !== yo.entidad?.id);
  const puedeCambiar = otrasEntidades.length > 0 || yo.usuario.esPlataforma;

  return (
    <>
      {enDemostracion() ? (
        <div className="franja franja--demo" role="status">
          <Icono nombre="desconectado" />
          <span>
            <strong>Modo demostración.</strong> La API no responde, así que lo que ves son datos de ejemplo. Nada de esto se guarda.
          </span>
        </div>
      ) : null}
      {yo.soporte ? (
        <div className="franja franja--soporte" role="status">
          <Icono nombre="escudo" />
          <span>
            Estás dentro de {yo.entidad?.nombre} como soporte de RASP. Todo lo que hagas queda registrado a tu nombre.
          </span>
          <Boton tamano="chico" onClick={() => void sesion.volverAPlataforma()}>
            Volver a la plataforma
          </Boton>
        </div>
      ) : null}

      <Boton variante="secundario" icono="menu" className="boton-menu-movil" onClick={() => setAbierta(!abierta)} aria-label="Abrir menú" />

      <div className="marco">
        <aside className={`barra ${abierta ? 'barra--abierta' : ''}`}>
          <div className="barra__marca">
            <img className="barra__logo" src={simbolo} alt="" />
            <div className="barra__nombre">
              <strong>Planilla</strong>
              <span>{yo.entidad ? 'Sin pagar dos veces' : 'Administración RASP'}</span>
            </div>
          </div>

          {yo.entidad || yo.usuario.esPlataforma ? (
            <button
              type="button"
              className="barra__entidad"
              onClick={() => puedeCambiar && setSelector(!selector)}
              aria-expanded={selector}
              disabled={!puedeCambiar}
            >
              <Icono nombre={yo.entidad ? 'escudo' : 'plataforma'} />
              <span>
                {yo.entidad?.nombre ?? 'Plataforma'}
                <small>{yo.rol ?? 'Soporte RASP'}</small>
              </span>
              {puedeCambiar ? <Icono nombre="cambiar" /> : null}
            </button>
          ) : null}

          {selector ? (
            <div className="selector-entidades">
              {otrasEntidades.map((entidad) => (
                <button
                  key={entidad.id}
                  type="button"
                  onClick={() => {
                    setSelector(false);
                    void sesion.cambiarEntidad(entidad.id);
                  }}
                >
                  <strong>{entidad.nombre}</strong>
                  <small>{entidad.rol}</small>
                </button>
              ))}
              {yo.usuario.esPlataforma && yo.entidad ? (
                <button
                  type="button"
                  onClick={() => {
                    setSelector(false);
                    void sesion.volverAPlataforma();
                  }}
                >
                  <strong>Administración de Planilla</strong>
                  <small>Plataforma RASP</small>
                </button>
              ) : null}
            </div>
          ) : null}

          <nav className="menu" aria-label="Principal">
            {yo.entidad ? (
              yo.menu.map((entrada, i) => (
                <NavLink
                  key={entrada.clave}
                  to={entrada.bloqueado ? `/no-incluido/${entrada.clave.toLowerCase()}` : entrada.ruta}
                  end={entrada.ruta === '/'}
                  onClick={() => setAbierta(false)}
                  className={({ isActive }) =>
                    `menu__item ${isActive ? 'menu__item--activo' : ''} ${entrada.bloqueado ? 'menu__item--bloqueado' : ''}`
                  }
                  style={{ '--i': i } as CSSProperties}
                >
                  <Icono nombre={entrada.icono} />
                  {entrada.nombre}
                  {entrada.bloqueado ? <Icono nombre="candado" clase="menu__candado" titulo="No está en tu plan" /> : null}
                </NavLink>
              ))
            ) : (
              <NavLink
                to="/"
                end
                className={({ isActive }) => `menu__item ${isActive ? 'menu__item--activo' : ''}`}
                style={{ '--i': 0 } as CSSProperties}
              >
                <Icono nombre="plataforma" />
                Entidades y planes
              </NavLink>
            )}
          </nav>

          <div className="barra__pie">
            <div className="barra__tema" role="radiogroup" aria-label="Tema de la interfaz">
              {TEMAS.map((opcion) => (
                <button
                  key={opcion.valor}
                  type="button"
                  role="radio"
                  aria-checked={tema === opcion.valor}
                  className={`barra__tema-opcion ${tema === opcion.valor ? 'barra__tema-opcion--activa' : ''}`}
                  onClick={() => cambiar(opcion.valor)}
                  title={opcion.nombre}
                  aria-label={opcion.nombre}
                >
                  <Icono nombre={opcion.icono} />
                </button>
              ))}
            </div>

            <div className="barra__persona">
              <Avatar nombre={yo.usuario.nombre} />
              <div>
                <strong>{yo.usuario.nombre}</strong>
                <small>{yo.usuario.email}</small>
              </div>
            </div>

            <div className="barra__herramientas">
              <NavLink
                to="/mi-cuenta"
                onClick={() => setAbierta(false)}
                className={({ isActive }) => `boton boton--fantasma boton--chico ${isActive ? 'boton--activo' : ''}`}
              >
                <Icono nombre="usuario" />
                <span>Mi cuenta</span>
              </NavLink>
            </div>

            {/* Salir tiene que verse sin buscarlo: botón entero con texto, no un ícono. */}
            <Boton variante="secundario" icono="salir" className="barra__salir" onClick={() => void sesion.salir()}>
              Cerrar sesión
            </Boton>
          </div>
        </aside>

        <main className="contenido" key={ubicacion.pathname.split('/')[1]}>
          <div className="contenido__ancho">{children}</div>
        </main>
      </div>
    </>
  );
}
